import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from '../ui/sheet';

const LOGO_WHITE = '/ofa_logo_dark.png';
const LOGO_DARK = '/ofa_logo_light.png';

function NavDropdown({ menu, location }) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <Link
        to={menu.mainHref}
        data-testid={`nav-menu-${menu.label.toLowerCase().replace(/\s/g, '-')}`}
        className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors duration-200 ${
          menu.activePaths.some(p => location.pathname === p)
            ? 'text-white border-b-2 border-white'
            : 'text-white/90 hover:text-white'
        }`}
        style={{ fontFamily: 'Poppins, sans-serif' }}
      >
        {menu.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </Link>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-md shadow-lg border border-[#E7EAEF] py-1 z-50">
          {menu.children.map((child) => (
            <Link
              key={child.href}
              to={child.href}
              data-testid={`nav-link-${child.href.split('?')[0].slice(1) || 'home'}`}
              onClick={() => setOpen(false)}
              className={`block px-4 py-2.5 text-sm transition-colors hover:bg-[#E7EAEF]/50 ${
                (child.href.includes('?') ? location.pathname + location.search === child.href : location.pathname === child.href)
                  ? 'text-[#2D78F6] font-semibold' : 'text-[#14274B]'
              }`}
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navigation() {
  const { t, language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navMenus = [
    {
      label: language === 'fr' ? 'Programmes' : 'Programs',
      mainHref: '/programs',
      children: [
        { href: '/programs', label: language === 'fr' ? 'Nos Programmes' : 'Our Programs' },
        { href: '/returning-families', label: language === 'fr' ? 'Quel parcours pour votre enfant ?' : 'Which track for your child?' },
      ],
      activePaths: ['/programs', '/returning-families'],
    },
    {
      label: language === 'fr' ? 'Fonctionnement' : 'How does it work?',
      mainHref: '/how-it-works',
      children: [
        { href: '/how-it-works', label: language === 'fr' ? 'Comment ça fonctionne ?' : 'How does it work?' },
        { href: '/difference', label: language === 'fr' ? 'La Différence OFA' : 'The OFA Difference' },
        { href: '/diploma', label: language === 'fr' ? 'Diplôme & Parcours' : 'Diploma & Pathways' },
      ],
      activePaths: ['/how-it-works', '/difference', '/diploma'],
    },
    {
      label: language === 'fr' ? 'Admissions' : 'Admissions',
      mainHref: '/admissions',
      children: [
        { href: '/admissions', label: language === 'fr' ? 'Processus d\'Admission' : 'Admission Process' },
        { href: '/tuition', label: language === 'fr' ? 'Frais de Scolarité' : 'Tuition & Fees' },
        { href: '/events', label: language === 'fr' ? 'Événements' : 'Events' },
        { href: '/faq', label: 'FAQ' },
      ],
      activePaths: ['/admissions', '/tuition', '/events', '/faq'],
    },
    {
      label: language === 'fr' ? 'À Propos' : 'About',
      mainHref: '/about',
      children: [
        { href: '/about', label: language === 'fr' ? 'Notre Histoire' : 'Our History' },
        { href: '/about?tab=concept', label: language === 'fr' ? 'Notre Concept' : 'Our Concept' },
        { href: '/about?tab=equipe', label: language === 'fr' ? 'Équipe Dirigeante' : 'Leadership Team' },
        { href: '/about?tab=emplois', label: language === 'fr' ? 'Rejoignez-nous' : 'Join Us' },
      ],
      activePaths: ['/about'],
    },
    {
      label: language === 'fr' ? 'Partenariats' : 'Partnerships',
      href: '/partnerships',
      activePaths: ['/partnerships'],
    },
  ];

  // All links flat for mobile
  const allMobileLinks = [
    { href: '/programs', label: language === 'fr' ? 'Programmes' : 'Programs', isHeader: true },
    { href: '/programs', label: language === 'fr' ? 'Nos Programmes' : 'Our Programs' },
    { href: '/returning-families', label: language === 'fr' ? 'Quel parcours pour votre enfant ?' : 'Which track for your child?' },
    { href: '/how-it-works', label: language === 'fr' ? 'Fonctionnement' : 'How does it work?', isHeader: true },
    { href: '/how-it-works', label: language === 'fr' ? 'Comment ça marche' : 'How It Works' },
    { href: '/difference', label: language === 'fr' ? 'La Différence OFA' : 'The OFA Difference' },
    { href: '/diploma', label: language === 'fr' ? 'Diplôme & Parcours' : 'Diploma & Pathways' },
    { href: '/admissions', label: language === 'fr' ? 'Admissions' : 'Admissions', isHeader: true },
    { href: '/admissions', label: language === 'fr' ? 'Processus d\'Admission' : 'Admission Process' },
    { href: '/tuition', label: language === 'fr' ? 'Frais de Scolarité' : 'Tuition & Fees' },
    { href: '/events', label: language === 'fr' ? 'Événements' : 'Events' },
    { href: '/faq', label: 'FAQ' },
    { href: '/about', label: language === 'fr' ? 'À Propos' : 'About', isHeader: true },
    { href: '/about', label: language === 'fr' ? 'Notre Histoire' : 'Our History' },
    { href: '/about?tab=equipe', label: language === 'fr' ? 'Équipe Dirigeante' : 'Leadership Team' },
    { href: '/partnerships', label: language === 'fr' ? 'Partenariats' : 'Partnerships', isHeader: true },
    { href: '/partnerships', label: language === 'fr' ? 'Devenir partenaire' : 'Become a partner' },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#2D78F6] shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <img src={LOGO_WHITE} alt="OFA France" className="h-9 md:h-11 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navMenus.map((menu) => (
              menu.children ? (
              <NavDropdown key={menu.label} menu={menu} location={location} />
              ) : (
              <Link
                key={menu.label}
                to={menu.href}
                data-testid={`nav-menu-${menu.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  menu.activePaths.some(p => location.pathname === p)
                    ? 'text-white border-b-2 border-white'
                    : 'text-white/90 hover:text-white'
                }`}
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {menu.label}
              </Link>
              )
            ))}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              data-testid="nav-language-toggle"
              className="flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium rounded transition-colors duration-200 text-white/90 hover:text-white hover:bg-white/10"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              <Globe className="h-4 w-4" />
              <span className="uppercase">{language === 'en' ? 'FR' : 'EN'}</span>
            </button>

            {/* CTA Button - Desktop */}
            <a href="https://admissions.ofalycee.fr" target="_blank" rel="noopener noreferrer" className="hidden md:block">
              <Button
                data-testid="nav-book-call-btn"
                className="font-medium transition-all duration-300 bg-white text-[#2D78F6] hover:bg-[#14274B] hover:text-white"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {t.nav.bookCall}
              </Button>
            </a>

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <button
                  data-testid="nav-mobile-menu-btn"
                  className="p-2 rounded-md transition-colors text-white hover:bg-white/10"
                >
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px] p-0">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between p-4 border-b border-[#14274B]/10">
                    <img src={LOGO_DARK} alt="OFA France" className="h-8 w-auto" />
                    <SheetClose asChild>
                      <button className="p-2 rounded-md hover:bg-[#E7EAEF] text-[#14274B]">
                        <X className="h-5 w-5" />
                      </button>
                    </SheetClose>
                  </div>

                  <div className="flex-1 overflow-y-auto py-4">
                    <div className="flex flex-col gap-0.5 px-4">
                      {allMobileLinks.map((link, i) => (
                        link.isHeader ? (
                          <p key={i} className="px-4 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-[#475569]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                            {link.label}
                          </p>
                        ) : (
                          <SheetClose key={i} asChild>
                            <Link
                              to={link.href}
                              data-testid={`nav-mobile-link-${link.href.split('?')[0].slice(1)}`}
                              className={`px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                                location.pathname === link.href
                                  ? 'bg-[#2D78F6]/10 text-[#2D78F6]'
                                  : 'text-[#14274B] hover:bg-[#E7EAEF]'
                              }`}
                              style={{ fontFamily: 'Poppins, sans-serif' }}
                            >
                              {link.label}
                            </Link>
                          </SheetClose>
                        )
                      ))}
                    </div>
                  </div>

                  <div className="p-4 border-t border-[#14274B]/10 space-y-3">
                    <SheetClose asChild>
                      <a href="https://admissions.ofalycee.fr" target="_blank" rel="noopener noreferrer" className="block">
                        <Button
                          data-testid="nav-mobile-book-call-btn"
                          className="w-full bg-[#2D78F6] text-white hover:bg-[#14274B]"
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        >
                          {t.nav.bookCall}
                        </Button>
                      </a>
                    </SheetClose>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
